import React from 'react';
import { Loader2, AlertTriangle, SearchX, RefreshCw } from 'lucide-react';

interface StateScreenProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export const LoadingState: React.FC<StateScreenProps> = ({
  title = 'Loading...',
  message = 'Please wait while we prepare your solution',
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="relative">
          <div className="w-20 h-20 mx-auto mb-6">
            <Loader2 className="w-20 h-20 text-blue-500 animate-spin" />
          </div>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-500">{message}</p>
        <div className="mt-6 flex justify-center gap-1">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

export const ErrorState: React.FC<StateScreenProps> = ({
  title = 'Something went wrong',
  message = 'We encountered an error while processing your request',
  onRetry,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-500 mb-6">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};

export const NotFoundState: React.FC<StateScreenProps> = ({
  title = 'No results found',
  message = 'We couldn\'t find any solution for your question',
  onRetry,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
          <SearchX className="w-10 h-10 text-amber-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-500 mb-6">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Try Another Question
          </button>
        )}
      </div>
    </div>
  );
};

export const InitialState: React.FC<StateScreenProps> = ({
  title = 'Welcome to mAIthZen',
  message = 'Enter a question below to get started with step-by-step solutions',
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-3">{title}</h2>
        <p className="text-gray-500 mb-6">{message}</p>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-left">
          <p className="text-sm text-gray-600 mb-3">Try asking questions like:</p>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Solve for x: 2x + 5 = 15</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Find the area of a triangle with base 10 and height 6</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>What is the derivative of x² + 3x?</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
